import { create } from 'zustand';

import { mobileBreakpoint } from "../../entrypoints/theme";
import useMobileBreakpoint from "@arctheme-hooks/useMobileBreakpoint";

type States = {
    currentMenu: string | null; // Tracks the currently open menu (e.g., 'ACCOUNT', 'CART')
    isMenuOpen: boolean; // Tracks whether any menu is open
};

type Reducers = {
    openMenu: (menuId: string, isMobile?: boolean) => void; // Opens a specific menu
    closeMenu: (isMobile?: boolean) => void; // Closes the currently open menu
    toggleMenu: (menuId: string) => void; // Toggles a specific menu
};

const initialState: States = {
    currentMenu: null,
    isMenuOpen: false,
};

const headerMenuStore = create<States & Reducers>((set, get) => ({
    ...initialState,

    // Open a specific menu
    openMenu: (menuId: string, isMobile?: boolean, allowFilter = true) => {
        const { currentMenu } = get();

        if (currentMenu !== menuId) {
            if (currentMenu  && isMobile) {
                document.body.classList.remove(`${currentMenu.toLowerCase()}-open`);
            }
        }
        set({ currentMenu: menuId, isMenuOpen: true });

        if (isMobile) {
            document.body.classList.add(`${menuId.toLowerCase()}-open`);
        }

        if (allowFilter) {
            Alpine?.store('overlay')?.show();
        }
    },

    // Close the current menu
    closeMenu: (isMobile?: boolean) => {
        const { currentMenu } = get();

        // Remove the class from the body when the menu is closed
        if (currentMenu && isMobile) {
            document.body.classList.remove(`${currentMenu.toLowerCase()}-open`);
        }

        set({ currentMenu: null, isMenuOpen: false });

        Alpine?.store('overlay')?.hide();
    },

    // Toggle a specific menu
    toggleMenu: (menuId: string) => {
        const { currentMenu } = get();
        if (currentMenu === menuId) {
            set({ currentMenu: null, isMenuOpen: false });
        } else {
            set({ currentMenu: menuId, isMenuOpen: true });
        }
    },
    // Set up the body click listener
    setupBodyClickListener: (isMobile?: boolean) => {
        const ignoredClasses = [
            "menu-container",
            "minicart",
            "account-popup__sidebar",
            "currency-selector__wrapper",
            "currency-selector",
            "megamenu-nav-list",
            "predictive-search-wrapper",
            "predictive-search__container",
            "search-input",
            "product-results__filters-container",
            "header", // Add 'header' to the ignored classes to avoid immediate menu closure
            "header-container" // Add 'header' to the ignored classes to avoid immediate menu closure
        ];

        const handleClickOutside = (event: MouseEvent) => {
            const { currentMenu } = get();

            // Check if the clicked target or any of its parents have the ignored classes
            const isIgnoredClick = ignoredClasses.some((cls) =>
                event.target instanceof HTMLElement
                    ? event.target.closest(`.${cls}`)
                    : false
            );

            if (isIgnoredClick) {
                return; // Ignore the click if it's within any of the ignored elements
            }

            // Close the menu if the click is outside
            if (currentMenu) {
                if (isMobile) {
                    document.body.classList.remove(`${currentMenu.toLowerCase()}-open`);
                }
                Alpine?.store('overlay')?.hide();
                set({ currentMenu: null, isMenuOpen: false });
            }
        };

        const handleMouseExit = (event: MouseEvent) => {
            const { currentMenu } = get();
            // Check if the mouse enters any ignored classes or the header
            const isInsideIgnoredArea = ignoredClasses.some((cls) =>
                event.target instanceof HTMLElement
                    ? event.target.closest(`.${cls}`)
                    : false
            );

            if (!isInsideIgnoredArea && currentMenu) {
                Alpine?.store('overlay')?.hide();
                set({ currentMenu: null, isMenuOpen: false });
            }
        };

        // Attach the event listeners to the document body
        // document.body.addEventListener("click", handleClickOutside);
        window.addEventListener("mousemove", handleMouseExit);

        // Clean up the event listeners when the store is no longer used
        return () => {
            // document.body.removeEventListener("click", handleClickOutside);
            document.body.removeEventListener("mousemove", handleMouseExit);
        };
    },
}));

export default headerMenuStore;
